import { normalizeMaterialKey } from "./manualPriceProvider";

export const OPSUCHT_MARKET_API_BASE = "https://api.opsucht.net/market";
export const OPSUCHT_MARKET_CACHE_KEY = "opsucht_market_prices_v1";

export const PRICE_SOURCE_OPSUCHT_API = "opsuchtApi";
export const PRICE_SOURCE_CACHE = "cache";
export const PRICE_SOURCE_MISSING = "missing";

const parseCachedPrices = () => {
  try {
    const cachedRaw = window.localStorage.getItem(OPSUCHT_MARKET_CACHE_KEY);
    return cachedRaw === null ? null : JSON.parse(cachedRaw);
  } catch (error) {
    console.warn("Could not read OPSUCHT market cache", error);
    return null;
  }
};

const writeCachedPrices = (prices) => {
  try {
    window.localStorage.setItem(
      OPSUCHT_MARKET_CACHE_KEY,
      JSON.stringify({
        cachedAt: Date.now(),
        prices,
      })
    );
  } catch (error) {
    console.warn("Could not write OPSUCHT market cache", error);
  }
};

const normalizePriceValue = (value) => {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value === "string") {
    const parsed = Number(value.replace(/[^\d.]/g, ""));
    return Number.isFinite(parsed) ? parsed : null;
  }
  if (Array.isArray(value)) {
    const sellEntry = value.find((entry) => entry && String(entry.orderSide || "").toUpperCase() === "SELL" && normalizePriceValue(entry.price) > 0);
    if (sellEntry) return normalizePriceValue(sellEntry.price);
    const buyEntry = value.find((entry) => entry && String(entry.orderSide || "").toUpperCase() === "BUY" && normalizePriceValue(entry.price) > 0);
    if (buyEntry) return normalizePriceValue(buyEntry.price);
    return null;
  }
  if (value && typeof value === "object") {
    const priceFields = ["price", "sell", "buy", "average", "avg", "current", "value"];
    for (const fieldName of priceFields) {
      if (fieldName in value) {
        const normalized = normalizePriceValue(value[fieldName]);
        if (normalized !== null) return normalized;
      }
    }
  }
  return null;
};

export const normalizeOpsuchtPrices = (pricesPayload) => {
  const normalizedPrices = {};

  const addPrice = (material, priceLike) => {
    const normalizedMaterial = normalizeMaterialKey(material);
    const normalizedPrice = normalizePriceValue(priceLike);
    if (normalizedMaterial !== "" && normalizedPrice !== null) {
      normalizedPrices[normalizedMaterial] = normalizedPrice;
    }
  };

  const visitObjectEntries = (objectToVisit) => {
    for (const [key, value] of Object.entries(objectToVisit)) {
      const directPrice = normalizePriceValue(value);
      if (directPrice !== null) {
        addPrice(key, directPrice);
      } else if (value && typeof value === "object") {
        if ("material" in value || "item" in value || "name" in value || "id" in value) {
          addPrice(value.material || value.item || value.name || value.id, value.price !== undefined ? value.price : value);
        } else {
          visitObjectEntries(value);
        }
      }
    }
  };

  if (Array.isArray(pricesPayload)) {
    pricesPayload.forEach((entry) => {
      if (entry && typeof entry === "object") {
        addPrice(entry.material || entry.item || entry.name || entry.id, entry.price !== undefined ? entry.price : entry);
      }
    });
  } else if (pricesPayload && typeof pricesPayload === "object") {
    const nestedPrices = pricesPayload.prices || pricesPayload.data || pricesPayload.items;
    if (nestedPrices && nestedPrices !== pricesPayload) {
      return normalizeOpsuchtPrices(nestedPrices);
    }
    visitObjectEntries(pricesPayload);
  }

  return normalizedPrices;
};

export const loadOpsuchtMarketPrices = async () => {
  try {
    const response = await fetch(`${OPSUCHT_MARKET_API_BASE}/prices`);
    if (!response.ok) {
      throw new Error(`OPSUCHT market returned ${response.status}`);
    }
    const payload = await response.json();
    const prices = normalizeOpsuchtPrices(payload);
    if (Object.keys(prices).length !== 0) {
      writeCachedPrices(prices);
    }
    return { prices, source: PRICE_SOURCE_OPSUCHT_API, cachedAt: Date.now(), error: null };
  } catch (error) {
    const cached = parseCachedPrices();
    if (cached && cached.prices) {
      return { prices: cached.prices, source: PRICE_SOURCE_CACHE, cachedAt: cached.cachedAt || null, error };
    }
    return { prices: {}, source: PRICE_SOURCE_MISSING, cachedAt: null, error };
  }
};

export const createOpsuchtMarketProvider = ({ prices, source }) => {
  return {
    source,
    getPrice(material) {
      const normalizedMaterial = normalizeMaterialKey(material);
      if (normalizedMaterial in prices) return prices[normalizedMaterial];
      const minecraftMaterial = normalizeMaterialKey(`minecraft:${material}`);
      return minecraftMaterial in prices ? prices[minecraftMaterial] : null;
    },
  };
};
